import i18n from 'i18n'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { deliveriesAPI } from 'api/deliveries'
import { driversAPI } from 'api/drivers'
import { isOfflineQueuedFulfilledAction } from 'helpers/offlineAction'
import { branchChanged } from './branchSlice'

interface Driver {
 id: string
 name: string
 phone: string
 branch_id?: string
 vehicle_info?: string
 vehicle_plate?: string
 status: string
 is_active: boolean
 active_deliveries_count?: number
}

interface Delivery {
 id: string
 sale_id: number
 driver_id?: string
 branch_id?: string
 delivery_address: string
 customer_phone?: string
 delivery_fee: number
 status: string
 assigned_at?: string
 picked_up_at?: string
 delivered_at?: string
 notes?: string
 collected_amount: number
 sale?: any
 driver?: Driver
 branch?: any
 created_at: string
}

interface DeliveriesState {
 data: Delivery[]
 drivers: Driver[]
 todaySummary: any
 loading: boolean
 error: string | null
 pagination: { total: number; per_page: number; current_page: number; last_page: number }
}

const initialState: DeliveriesState = {
 data: [],
 drivers: [],
 todaySummary: null,
 loading: false,
 error: null,
 pagination: { total: 0, per_page: 20, current_page: 1, last_page: 1 },
}

export const fetchDeliveries = createAsyncThunk(
 'deliveries/fetchDeliveries',
 async (params: any, { rejectWithValue }) => {
 try {
 const response = await deliveriesAPI.getAll(params)
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.deliveries.fetchFailed'))
 }
 }
)

export const fetchTodayDeliveries = createAsyncThunk(
 'deliveries/fetchToday',
 async (params: any, { rejectWithValue }) => {
 try {
 const response = await deliveriesAPI.getToday(params)
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.deliveries.fetchTodayFailed'))
 }
 }
)

export const assignDriver = createAsyncThunk(
 'deliveries/assignDriver',
 async ({ id, driver_id }: { id: string; driver_id: string }, { rejectWithValue }) => {
 try {
 const response = await deliveriesAPI.assign(id, { driver_id })
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.deliveries.assignDriverFailed'))
 }
 }
)

export const updateDeliveryStatus = createAsyncThunk(
 'deliveries/updateStatus',
 async ({ id, ...data }: { id: string; status: string; notes?: string; collected_amount?: number; collection_note?: string; partial_collection_waiver?: boolean }, { rejectWithValue }) => {
 try {
 const response = await deliveriesAPI.updateStatus(id, data)
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.deliveries.updateStatusFailed'))
 }
 }
)

export const fetchActiveDrivers = createAsyncThunk(
 'deliveries/fetchDrivers',
 async (params: any, { rejectWithValue }) => {
 try {
 const response = await driversAPI.getActive(params)
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.deliveries.fetchDriversFailed'))
 }
 }
)

export const createDriver = createAsyncThunk(
 'deliveries/createDriver',
 async (data: any, { rejectWithValue }) => {
 try {
 const response = await driversAPI.create(data)
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.deliveries.createDriverFailed'))
 }
 }
)

export const updateDriver = createAsyncThunk(
 'deliveries/updateDriver',
 async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
 try {
 const response = await driversAPI.update(id, data)
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.deliveries.updateDriverFailed'))
 }
 }
)

export const toggleDriverStatus = createAsyncThunk(
 'deliveries/toggleDriver',
 async (id: string, { rejectWithValue }) => {
 try {
 const response = await driversAPI.toggleStatus(id)
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.deliveries.toggleDriverFailed'))
 }
 }
)

const deliveriesSlice = createSlice({
 name: 'deliveries',
 initialState,
 reducers: {
 clearDeliveries: (state) => {
 state.data = []
 state.todaySummary = null
 state.error = null
 },
 },
 extraReducers: (builder) => {
 builder
 .addCase(fetchDeliveries.pending, (state) => { state.loading = true; state.error = null })
 .addCase(fetchDeliveries.fulfilled, (state, action) => {
 state.loading = false
 state.data = action.payload.deliveries
 state.pagination = action.payload.pagination
 })
 .addCase(fetchDeliveries.rejected, (state, action) => { state.loading = false; state.error = action.payload as string })
 .addCase(fetchTodayDeliveries.fulfilled, (state, action) => {
 state.todaySummary = action.payload.summary
 state.data = action.payload.deliveries
 })
 .addCase(assignDriver.fulfilled, (state, action) => {
 if (isOfflineQueuedFulfilledAction(action)) {
 return
 }
 const idx = state.data.findIndex((d) => d.id === action.payload.id)
 if (idx !== -1) state.data[idx] = action.payload
 })
 .addCase(updateDeliveryStatus.fulfilled, (state, action) => {
 if (isOfflineQueuedFulfilledAction(action)) {
 return
 }
 const idx = state.data.findIndex((d) => d.id === action.payload.id)
 if (idx !== -1) state.data[idx] = action.payload
 })
 .addCase(fetchActiveDrivers.fulfilled, (state, action) => { state.drivers = action.payload })
 .addCase(createDriver.fulfilled, (state, action) => {
 if (isOfflineQueuedFulfilledAction(action)) {
 return
 }
 const created = action.payload as { id?: string; _offlineOptimistic?: boolean }
 if (created?._offlineOptimistic || !created?.id) {
 return
 }
 state.drivers.push(action.payload)
 })
 .addCase(updateDriver.fulfilled, (state, action) => {
 if (isOfflineQueuedFulfilledAction(action)) {
 return
 }
 const updated = action.payload
 if (!updated?.id) return
 const idx = state.drivers.findIndex((d) => d.id === updated.id)
 if (idx !== -1) state.drivers[idx] = { ...state.drivers[idx], ...updated }
 })
 .addCase(toggleDriverStatus.fulfilled, (state, action) => {
 if (isOfflineQueuedFulfilledAction(action)) {
 return
 }
 const idx = state.drivers.findIndex((d) => d.id === action.payload.id)
 if (idx !== -1) state.drivers[idx] = action.payload
 })
 .addCase(branchChanged, (state) => {
 Object.assign(state, initialState)
 })
 },
})

export const { clearDeliveries } = deliveriesSlice.actions
export default deliveriesSlice.reducer
